import { Component } from 'react';
import * as PropTypes from 'prop-types';
import { omit } from 'lodash';
import BEM from 'lib/bem';
import { getDuration } from 'models/asset';
import { Button, Spinner, Label } from '@schibsted-svp/react-ui';
import VideoCard from 'components/core/VideoCard';
import { Playback as RhfPlayback } from 'components/AssetForm/containers/Playback';
import Playback from '../edit/Form/Playback';
import ReduxFormPlayback from '../edit/Form/Playback/ReduxFormPlayback';
import ClipCard from './ClipCard';
import './Clips.scss';
import { ClipDownload, formatSecondsToHHMMSS } from './ClipDownload/ClipDownload';

const bem = new BEM('Clips');

const getDetailedDuration = (asset) => {
    return getDuration(asset, 'ms') / 1000;
};

export default class Clips extends Component {
    static propTypes = {
        className: PropTypes.string,
        provider: PropTypes.string.isRequired,
        asset: PropTypes.object.isRequired,
        originAsset: PropTypes.shape({
            asset: PropTypes.object,
            loading: PropTypes.bool,
        }).isRequired,
        clipList: PropTypes.shape({
            assetList: PropTypes.arrayOf(PropTypes.number),
            loading: PropTypes.bool,
            hasMore: PropTypes.bool,
            nextUrl: PropTypes.string,
        }).isRequired,
        downloadableClips: PropTypes.objectOf(
            PropTypes.shape({
                url: PropTypes.string,
                isReady: PropTypes.bool,
            })
        ),
        player: PropTypes.shape({
            duration: PropTypes.number,
        }),
        downloadableClipsProgress: PropTypes.number,
        fetchAsset: PropTypes.func.isRequired,
        fetchClips: PropTypes.func.isRequired,
        createClip: PropTypes.func.isRequired,
        clipDownload: PropTypes.func.isRequired,
        isClipDownloadInProgress: PropTypes.bool.isRequired,
        isClipDownloadPending: PropTypes.bool,
        trimVideo: PropTypes.func.isRequired,
        resetTrim: PropTypes.func.isRequired,
        ready: PropTypes.bool.isRequired,
        activeLive: PropTypes.bool.isRequired,
        isCreateClipPending: PropTypes.bool.isRequired,
        isTrimPending: PropTypes.bool.isRequired,
        isTrimInProgress: PropTypes.bool.isRequired,
        isConversionInProgress: PropTypes.bool.isRequired,
        isResetInProgress: PropTypes.bool,
        originalStreamUrls: PropTypes.string,
        rhfControl: PropTypes.object,
        rhfIsSubmitting: PropTypes.bool,
    };

    static defaultProps = {
        className: undefined,
        isResetInProgress: false,
        originalStreamUrls: undefined,
        rhfControl: {},
        rhfIsSubmitting: false,
        player: null,
    };

    state = {
        clipTimeError: {
            startError: null,
            endError: null,
        },
    };

    componentDidMount() {
        this.fetchOriginAsset();
        this.fetchAssetClips();
    }

    onLoadMoreClick = () => {
        this.fetchAssetClips();
    };

    /**
     * Returns the origin asset id
     * @return {Number|undefined}
     */
    get originAssetId() {
        const { asset } = this.props;

        return asset.additional.originAssetId;
    }

    /**
     * @return {Boolean}
     */
    get loading() {
        const { clipList, originAsset } = this.props;

        if (!this.originAssetId) {
            return clipList.loading;
        }

        return originAsset.loading || clipList.loading;
    }

    handleLiveArchiveClipCuts = (asset, player, playback, clipCallback) => {
        const playerDuration = Math.floor(player.duration / 60) * 60;

        const assetDuration = asset.duration / 1000;

        const currentDuration = assetDuration || playerDuration;

        const cutRangeBeginTime = playback.begin ?? 0;

        const cutRangeEndTime = playback.end || currentDuration;

        this.setState(() => ({
            clipTimeError: {
                startError: null,
                endError: null,
            },
        }));

        if (cutRangeBeginTime >= currentDuration) {
            this.setState((prevState) => ({
                clipTimeError: {
                    ...prevState.clipTimeError,
                    startError: `Start time should be lower, than ${formatSecondsToHHMMSS(assetDuration || Math.floor(playerDuration))}`,
                },
            }));

            return;
        }

        if (cutRangeEndTime > currentDuration) {
            this.setState((prevState) => ({
                clipTimeError: {
                    ...prevState.clipTimeError,
                    endError: `End time shouldn't be higher, than ${formatSecondsToHHMMSS(assetDuration || Math.floor(playerDuration))}`,
                },
            }));

            return;
        }

        if (cutRangeBeginTime >= cutRangeEndTime) {
            this.setState((prevState) => ({
                clipTimeError: { ...prevState.clipTimeError, startError: 'Start time should be lower than end time' },
            }));

            return;
        }

        const cutRangeInput = {
            begin: cutRangeBeginTime,
            end: cutRangeEndTime,
        };

        clipCallback(cutRangeInput);
    };

    handleClipCreate = (playback, isLiveArchive = false, liveArchiveExpiration = undefined) => {
        const { asset, provider, createClip, player } = this.props;

        const currentTime = Date.now();

        const applyLiveArchive = isLiveArchive && liveArchiveExpiration && liveArchiveExpiration > currentTime;

        if (applyLiveArchive) {
            const clipCreateCallback = (cutRangeInput) => createClip(asset.id, provider, cutRangeInput);

            this.handleLiveArchiveClipCuts(asset, player, playback, clipCreateCallback);

            return;
        }

        createClip(asset.id, provider, playback);
    };

    handleClipDownload = (playback) => {
        const { asset, provider, clipDownload, player } = this.props;

        const clipDownloadCallback = (cutRangeInput) =>
            clipDownload({
                provider,
                id: asset.id,
                cutRange: cutRangeInput,
            });

        this.handleLiveArchiveClipCuts(asset, player, playback, clipDownloadCallback);
    };

    handleVideoTrim = (playback) => {
        const { asset, provider, trimVideo } = this.props;

        trimVideo(asset.id, provider, playback);
    };

    handleResetTrim = () => {
        const { asset, provider, resetTrim, originalStreamUrls } = this.props;

        const parsedOriginalStreamUrls = JSON.parse(originalStreamUrls);

        const previousData = {
            streamUrls: parsedOriginalStreamUrls,
            additional: {
                ...asset.additional,
                metadata: omit(asset.additional?.metadata, ['originalStreamUrls', 'previousStreamUrls']),
            },
        };

        resetTrim({ assetId: asset.id, provider, previousData });
    };

    fetchOriginAsset() {
        const { provider, originAsset, fetchAsset } = this.props;

        if (this.originAssetId && !originAsset.asset && !originAsset.loading) {
            fetchAsset({
                id: this.originAssetId,
                provider,
            });
        }
    }

    fetchAssetClips() {
        const { asset, provider, clipList, fetchClips } = this.props;
        const { assetList, loading, nextUrl, hasMore } = clipList;

        if (!loading && (!assetList || (assetList.length && hasMore))) {
            fetchClips(this.originAssetId || asset.id, provider, nextUrl);
        }
    }

    renderPlaybackForm = () => {
        const {
            asset,
            provider,
            ready,
            activeLive,
            isClipDownloadInProgress,
            isClipDownloadPending,
            isCreateClipPending,
            isTrimPending,
            isTrimInProgress,
            isConversionInProgress,
            isResetInProgress,
            originalStreamUrls,
            rhfControl,
            rhfIsSubmitting,
        } = this.props;
        const duration = asset.streamType !== 'live' ? getDetailedDuration(asset) : undefined;
        const disabled = this.loading || !ready || isResetInProgress;

        if (this.originAssetId) {
            return (
                <RhfPlayback
                    className={bem.element('form')}
                    component={ReduxFormPlayback}
                    disabled={disabled}
                    duration={duration}
                    control={rhfControl}
                    isSubmitting={rhfIsSubmitting}
                />
            );
        }

        // Cannot handle a clip creation from a non active live video
        const canCreateClip = asset.streamType !== 'live' || activeLive;
        const canDownloadClip = ['live', 'wasLive'].includes(asset.streamType) || activeLive;
        const canVideoTrim = asset.streamType === 'wasLive';
        const canResetTrim = canVideoTrim && originalStreamUrls;
        if (!canCreateClip && !canDownloadClip && !canVideoTrim) {
            return <div className={bem.element('info')}>Live video has to be running to allow clip creation.</div>;
        }

        return (
            <Playback
                assetId={asset.id}
                provider={provider}
                assetStreamType={asset.streamType}
                className={bem.element('form')}
                disabled={disabled}
                duration={duration}
                onClipCreate={canCreateClip ? this.handleClipCreate : undefined}
                onClipDownload={canDownloadClip ? this.handleClipDownload : undefined}
                clipDownloadDisabled={isTrimInProgress || isConversionInProgress || isClipDownloadInProgress}
                clipDownloadLoading={isClipDownloadPending}
                clipDownloadRangeErrors={this.state.clipTimeError}
                onVideoTrim={canVideoTrim ? this.handleVideoTrim : undefined}
                onResetTrim={canResetTrim ? this.handleResetTrim : undefined}
                createClipLoading={isCreateClipPending}
                trimLoading={isTrimPending}
                trimDisabled={isTrimInProgress || isConversionInProgress || isClipDownloadInProgress}
                resetTrimLoading={isResetInProgress}
                precise
            />
        );
    };

    renderClipList() {
        const { asset, originAsset, clipList, provider, downloadableClips, downloadableClipsProgress } = this.props;
        const { assetList, loading, hasMore } = clipList;

        const clips = assetList && assetList.filter((id) => id !== asset.id);

        const showClips = clips != null && clips.length > 0;
        const showMoreButton = !loading && clips != null && clips.length > 0 && hasMore;
        const showOriginAsset = this.originAssetId && originAsset.asset != null;

        const showDownloadableClips = downloadableClips != null;

        if (this.loading) {
            return <Spinner containerClassName={bem.element('spinner')} />;
        }

        return (
            <>
                {showDownloadableClips && (
                    <div className={bem.element('clip-download')}>
                        <Label className={bem.element('clip-download-title')}>Clips ready for download</Label>
                        <div className={bem.element('clip-download-row')}>
                            <Label className={bem.element('clip-download-label')}>Begin</Label>
                            <Label className={bem.element('clip-download-label')}>End</Label>
                        </div>
                        {Object.keys(downloadableClips)
                            .reverse()
                            .map((downloadableClipKey) => (
                                <ClipDownload
                                    key={downloadableClipKey}
                                    assetId={asset.id}
                                    provider={provider}
                                    beginTimeToEndTime={downloadableClipKey}
                                    clipData={downloadableClips[downloadableClipKey]}
                                    progress={downloadableClipsProgress}
                                />
                            ))}
                    </div>
                )}
                {showClips && (
                    <div className={bem.element('grid')}>
                        {clips.map((clipId) => (
                            <ClipCard
                                key={`${provider}-asset-${clipId}`}
                                provider={provider}
                                id={clipId}
                                originAssetId={!originAsset.asset?.id ? asset.id : null}
                                data-testid="clipVideoCard"
                            />
                        ))}

                        {showMoreButton && (
                            <Button
                                className={bem.element('load-more-button')}
                                size="small"
                                type="button"
                                variant="standard"
                                onClick={this.onLoadMoreClick}
                            >
                                Load more
                            </Button>
                        )}
                    </div>
                )}
                {showOriginAsset && (
                    <div className={bem.element('original-container')}>
                        <span className={bem.element('heading')}>ORIGINAL VIDEO</span>
                        <VideoCard
                            readOnly
                            className={bem.element('original-video')}
                            variant="simple"
                            provider={provider}
                            id={originAsset.asset.id}
                            data-testid="clipVideoCard"
                        />
                    </div>
                )}
            </>
        );
    }

    render() {
        const { className } = this.props;

        return (
            <section className={bem.block(undefined, className)}>
                {this.renderPlaybackForm()}
                {this.renderClipList()}
            </section>
        );
    }
}
